import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

// SoloKeys: replacement for assets/sticky-add-to-cart.js in the installed theme.
// Options are read from the main picker. Shopify resolves the variant and price.
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];
  #abortController;
  #buyObserver;
  #bottomObserver;
  #formObserver;
  #section;
  #stuck = false;
  #hiddenByBottom = false;
  #quantity = 1;
  #generation = 0;
  #pending = false;
  #failed = false;
  #cartPending = false;
  #buyNow = false;
  #error = '';
  #optionsSignature = '';

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#section = this.closest('.shopify-section');
    this.#buyNow = /comprar ahora|buy now/i.test(this.refs.addToCartButton.textContent)
      || this.refs.addToCartButton.hasAttribute('onclick');
    this.#quantity = Number(this.dataset.initialQuantity) || 1;
    this.#installStyles();
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCart, { signal });
    document.addEventListener(StandardEvents.cartError, this.#onCart, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    // Capture prevents the old inline /cart/ID redirect from bypassing the form.
    this.addEventListener('click', this.#onClick, { capture: true, signal });
    this.#formObserver = new MutationObserver(() => this.#syncButton());
    const form = this.#getProductForm();
    if (form) this.#formObserver.observe(form, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'hidden'],
    });
    this.#renderControls();
    this.#setupIntersectionObserver();
    customElements.whenDefined('shopify-chat').then(() => {
      if (!signal.aborted && this.#isChatActive()) this.#setStuck(false);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.#abortController?.abort();
    this.#buyObserver?.disconnect();
    this.#bottomObserver?.disconnect();
    this.#formObserver?.disconnect();
  }

  #getProductForm() {
    return [...(this.#section?.querySelectorAll('product-form-component') || [])]
      .find(form => form.dataset.productId === this.dataset.productId) || null;
  }

  #getPicker() {
    return [...(this.#section?.querySelectorAll('variant-picker') || [])]
      .find(picker => picker.dataset.productId === this.dataset.productId
        && !picker.closest('product-card, quick-add-dialog')) || null;
  }

  #getPurchaseButton() {
    const form = this.#getProductForm();
    return (this.#buyNow && form?.querySelector('direct-checkout-button button'))
      || form?.querySelector('[ref="addToCartButton"]') || null;
  }

  #readGroups() {
    const picker = this.#getPicker();
    if (!picker) return [];
    return [...picker.querySelectorAll('fieldset, select')].flatMap(control => {
      // A select inside a fieldset belongs to its own select group.
      if (control.tagName === 'FIELDSET' && control.querySelector('select')) return [];
      const isSelect = control.tagName === 'SELECT';
      const items = [...control.querySelectorAll(isSelect ? 'option' : 'input[type="radio"]')]
        .filter(item => item.dataset.optionValueId);
      if (!items.length) return [];
      return [{
        name: items[0].dataset.optionName
          || control.querySelector('legend')?.textContent.trim() || 'Variante',
        items: items.map(item => ({
          id: item.dataset.optionValueId,
          value: item.value,
          selected: isSelect ? item.selected : item.checked,
          disabled: item.disabled,
        })),
      }];
    });
  }

  #renderControls() {
    const button = this.refs.addToCartButton;
    button.removeAttribute('onclick');
    button.removeAttribute('on:click');
    const groups = this.#readGroups();
    const signature = JSON.stringify(groups.map(group => [group.name, group.items.map(item => [item.id, item.value])]));
    let wrapper = this.querySelector('[data-sk-sticky-options]');
    const title = this.querySelector('.sticky-add-to-cart__variant');
    if (title) title.hidden = groups.length > 0;
    if (signature !== this.#optionsSignature || (!wrapper && groups.length)) {
      wrapper?.remove();
      this.#optionsSignature = signature;
      if (groups.length) {
        wrapper = document.createElement('div');
        wrapper.dataset.skStickyOptions = '';
        wrapper.className = 'sk-sticky-options';
        groups.forEach((group, index) => {
          const compact = group.items.length <= 4;
          const label = document.createElement(compact ? 'div' : 'label');
          label.className = 'sk-sticky-option';
          const caption = document.createElement('span');
          caption.textContent = group.name;
          label.append(caption);
          if (compact) {
            const choices = document.createElement('div');
            choices.className = 'sk-sticky-choices';
            choices.setAttribute('role', 'group');
            choices.setAttribute('aria-label', `${group.name} — barra de compra`);
            group.items.forEach(item => {
              const choice = document.createElement('button');
              choice.type = 'button';
              choice.className = 'sk-sticky-choice';
              choice.dataset.skStickyChoice = item.id;
              choice.textContent = item.value;
              choices.append(choice);
            });
            label.append(choices);
          } else {
            const select = document.createElement('select');
            select.dataset.skStickyOption = String(index);
            select.setAttribute('aria-label', `${group.name} — barra de compra`);
            group.items.forEach(item => {
              const option = document.createElement('option');
              option.value = item.id;
              option.textContent = item.value;
              option.selected = item.selected;
              option.disabled = item.disabled;
              select.append(option);
            });
            label.append(select);
            }
          wrapper.append(label);
        });
        this.querySelector('.sticky-add-to-cart__info')?.append(wrapper);
      }
    }
    // Keep the same interactive elements and focus after a response arrives.
    const items = groups.flatMap(group => group.items);
    this.querySelectorAll('[data-sk-sticky-choice]').forEach(choice => {
      const item = items.find(item => item.id === choice.dataset.skStickyChoice);
      choice.disabled = !item || item.disabled;
      choice.setAttribute('aria-pressed', String(Boolean(item?.selected)));
    });
    this.querySelectorAll('[data-sk-sticky-option]').forEach(select => {
      const group = groups[Number(select.dataset.skStickyOption)];
      [...select.options].forEach(option => {
        const item = group?.items.find(item => item.id === option.value);
        option.disabled = !item || item.disabled;
        option.selected = Boolean(item?.selected);
      });
    });
    this.#getStatus().textContent = this.#error;
    this.#syncButton();
  }

  #getStatus() {
    let status = this.querySelector('[data-sk-sticky-status]');
    if (!status) {
      status = document.createElement('div');
      status.dataset.skStickyStatus = '';
      status.className = 'sk-sticky-status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      this.refs.stickyBar.append(status);
    }
    return status;
  }

  #onChange = event => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-sk-sticky-option')) return;
    this.#selectNativeOption(select.value);
  };

  #selectNativeOption(value) {
    const picker = this.#getPicker();
    const nativeOption = [...(picker?.querySelectorAll('[data-option-value-id]') || [])]
      .find(option => option.dataset.optionValueId === value);
    if (!nativeOption || nativeOption.disabled) {
      this.#renderControls();
      return;
    }
    // A failed request can be retried by selecting the same visible value.
    if (!this.#failed && (nativeOption.checked || nativeOption.selected)) return;
    this.#pending = true;
    this.#failed = false;
    this.#error = '';
    this.#getStatus().textContent = '';
    this.#syncButton();
    if (nativeOption.tagName === 'OPTION') {
      const nativeSelect = nativeOption.closest('select');
      nativeSelect.value = nativeOption.value;
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // Dispatch through the real picker, including its Shopify metadata.
      nativeOption.checked = true;
      nativeOption.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #onProductSelect = async event => {
    if (!(event.target instanceof Element) || event.target !== this.#getPicker()) return;
    const generation = ++this.#generation;
    this.#pending = true;
    this.#failed = false;
    this.#error = '';
    this.#getStatus().textContent = '';
    this.#renderControls();
    try {
      const { detail } = await event.promise;
      if (generation !== this.#generation || !this.isConnected) return;
      if (!detail?.html || String(detail.productId) !== this.dataset.productId) {
        throw new Error('No se pudo actualizar la variante. Vuelve a seleccionarla.');
      }
      const source = [...detail.html.querySelectorAll('sticky-add-to-cart')]
        .find(sticky => sticky.dataset.productId === this.dataset.productId);
      const sourceBar = source?.querySelector('[ref="stickyBar"]');
      if (!sourceBar) throw new Error('No se pudo actualizar el precio. Vuelve a seleccionar la variante.');
      // Update only variant-dependent content; never replace the option controls.
      for (const selector of ['.sticky-add-to-cart__price', '.sticky-add-to-cart__image']) {
        const current = this.querySelector(selector);
        const next = sourceBar.querySelector(selector);
        if (current && next) morph(current, next, { childrenOnly: true });
      }
      for (const selector of ['.sticky-add-to-cart__title', '.sticky-add-to-cart__variant']) {
        const current = this.querySelector(selector);
        const next = sourceBar.querySelector(selector);
        if (current && next && current.textContent !== next.textContent) current.textContent = next.textContent;
      }
      this.dataset.variantAvailable = String(detail.resource?.available === true);
      this.dataset.currentVariantId = detail.resource?.id ? String(detail.resource.id) : '';
      this.refs.stickyBar.dataset.stuck = String(this.#stuck);
      // Give the native product-form listener time to update its hidden variant ID.
      await Promise.resolve();
      if (generation !== this.#generation || !this.isConnected) return;
      this.#pending = false;
      this.#renderControls();
    } catch (error) {
      if (generation !== this.#generation || !this.isConnected) return;
      this.#pending = false;
      this.#failed = true;
      this.#error = 'No se pudo actualizar la variante. Selecciónala de nuevo o recarga la página.';
      this.#getStatus().textContent = this.#error;
      this.#syncButton();
      if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart]', error);
    }
  };

  #syncButton() {
    const button = this.refs.addToCartButton;
    if (!button) return;
    const target = this.#getPurchaseButton();
    const form = this.#getProductForm();
    const actualId = form?.querySelector('input[name="id"]')?.value;
    const nativeAdd = form?.querySelector('[ref="addToCartButton"]');
    const available = this.dataset.variantAvailable === 'true';
    const busy = this.#pending || this.#cartPending || Boolean(target?.disabled && available);
    button.disabled = busy || this.#failed || !available || !target || nativeAdd?.disabled
      || !actualId || actualId !== this.dataset.currentVariantId;
    button.setAttribute('aria-busy', String(busy));
    this.dataset.skVariantPending = String(this.#pending);
    const label = button.querySelector('.add-to-cart-text__content > span:first-child');
    const text = !available ? 'No disponible'
      : this.#buyNow ? 'Comprar ahora' : 'Agregar al carrito';
    if (label && label.textContent !== text) label.textContent = text;
    this.refs.quantityNumber.textContent = String(this.#quantity);
    this.refs.quantityDisplay.style.display = available && this.#quantity > 1 ? 'inline' : 'none';
  }

  #onClick = event => {
    if (!(event.target instanceof Element)) return;
    const choice = event.target.closest('[data-sk-sticky-choice]');
    if (choice && this.contains(choice)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!choice.disabled) this.#selectNativeOption(choice.dataset.skStickyChoice);
      return;
    }
    if (event.target.closest('[ref="addToCartButton"]') !== this.refs.addToCartButton) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.handleAddToCartClick();
  };

  handleAddToCartClick = () => {
    this.#syncButton();
    if (this.refs.addToCartButton.disabled) return;
    const component = this.#getProductForm();
    const form = component?.querySelector('form');
    const target = this.#getPurchaseButton();
    if (!form || !target || target.disabled) return;
    // The current Autodesk field is optional and outside the form.
    // Trigger its existing synchronizer; validate a supplied email before buying.
    const email = this.#section?.querySelector('.sk-email-minimal__input');
    if (email) {
      email.dispatchEvent(new Event('input', { bubbles: true }));
      if (!email.checkValidity()) {
        email.scrollIntoView({ block: 'center', behavior: 'smooth' });
        email.focus({ preventScroll: true });
        email.reportValidity();
        return;
      }
    }
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    // Use the existing purchase control: same ID, quantity, properties and checkout.
    // No cart permalink, extra form, or independently calculated price.
    target.click();
    this.#syncButton();
  };

  #onCart = event => {
    if (!(event.target instanceof Element) || event.target.closest('product-form-component') !== this.#getProductForm()) return;
    if (!event.promise) return;
    this.#cartPending = true;
    this.#syncButton();
    const finish = () => {
      if (!this.isConnected) return;
      this.#cartPending = false;
      this.#syncButton();
    };
    event.promise.then(finish, finish);
  };

  #onQuantity = event => {
    if (event.detail.cartLine || !(event.target instanceof Element)
      || event.target.closest('product-form-component') !== this.#getProductForm()) return;
    this.#quantity = Number(event.detail.quantity) || 1;
    this.#syncButton();
  };

  #setupIntersectionObserver() {
    const buyButtons = this.#getProductForm()?.closest('.buy-buttons-block');
    if (!buyButtons) return;
    this.#buyObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (entry.isIntersecting) {
        this.#hiddenByBottom = false;
        this.#setStuck(false);
      } else if (entry.target.getBoundingClientRect().bottom < 0 && !this.#hiddenByBottom) {
        this.#setStuck(!this.#isChatActive());
      }
    });
    this.#buyObserver.observe(buyButtons);
    const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
    if (!footer) return;
    this.#bottomObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.#hiddenByBottom = entry.isIntersecting;
      this.#setStuck(!entry.isIntersecting && buyButtons.getBoundingClientRect().bottom < 0
        && !this.#isChatActive());
    }, { rootMargin: '200px 0px 0px 0px' });
    this.#bottomObserver.observe(footer);
  }

  #setStuck(value) {
    this.#stuck = value;
    this.refs.stickyBar.dataset.stuck = String(value);
  }

  #isChatActive() {
    return Boolean(customElements.get('shopify-chat') && document.querySelector('shopify-chat'));
  }

  #installStyles() {
    if (document.getElementById('sk-sticky-variant-styles')) return;
    const style = document.createElement('style');
    style.id = 'sk-sticky-variant-styles';
    style.textContent = `
      sticky-add-to-cart .sk-sticky-options {
        display: flex; flex-wrap: wrap; gap: 4px 8px; margin-block-start: 4px;
      }
      sticky-add-to-cart .sk-sticky-option {
        display: flex; align-items: center; flex-wrap: wrap; gap: 4px 6px;
        flex: 0 1 auto; min-width: 0; max-width: 100%;
        font-size: 10px; line-height: 1.2; color: var(--color-foreground);
      }
      sticky-add-to-cart .sk-sticky-choices {
        display: flex; flex-wrap: wrap; gap: 4px; min-width: 0;
      }
      sticky-add-to-cart .sk-sticky-choice {
        appearance: none; display: inline-flex; align-items: center; justify-content: center;
        position: relative; width: auto; min-width: 0; min-height: 28px; height: auto;
        margin: 0; padding: 4px 9px; border-radius: 5px;
        border: 1px solid rgb(var(--color-foreground-rgb) / .2);
        background: var(--color-background); color: var(--color-foreground);
        font-family: inherit; font-size: 12px; font-weight: 500; line-height: 1.2;
        text-transform: none; letter-spacing: normal; box-shadow: none;
        cursor: pointer; touch-action: manipulation;
        transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
      }
      sticky-add-to-cart .sk-sticky-choice::before,
      sticky-add-to-cart .sk-sticky-choice::after { display: none; }
      sticky-add-to-cart .sk-sticky-choice[aria-pressed='true'] {
        border-color: var(--color-foreground);
        background: rgb(var(--color-foreground-rgb) / .08);
      }
      sticky-add-to-cart .sk-sticky-choice:disabled { opacity: .45; cursor: default; }
      sticky-add-to-cart .sk-sticky-choice:focus-visible {
        outline: 2px solid currentColor; outline-offset: 2px;
      }
      sticky-add-to-cart .sk-sticky-option select {
        display: block; width: auto; max-width: 100%; min-width: 0; min-height: 28px;
        border: 1px solid rgb(var(--color-foreground-rgb) / .22); border-radius: 5px;
        padding: 4px 6px; font: inherit; font-size: 12px;
        color: var(--color-foreground); background-color: var(--color-background);
        cursor: pointer; appearance: auto;
      }
      sticky-add-to-cart .sk-sticky-option select:focus-visible {
        outline: 2px solid currentColor; outline-offset: 2px;
      }
      sticky-add-to-cart .sticky-add-to-cart__variant[hidden] { display: none !important; }
      sticky-add-to-cart .sk-sticky-status {
        grid-column: 1 / -1; flex-basis: 100%; color: var(--color-foreground);
        font-size: 12px; line-height: 1.35;
      }
      sticky-add-to-cart .sk-sticky-status:empty { display: none; }
      sticky-add-to-cart .sticky-add-to-cart__button:disabled { opacity: .6; cursor: wait; }
      sticky-add-to-cart[data-variant-available='false'] .sticky-add-to-cart__bar[data-stuck='true'] {
        display: grid; opacity: 1; visibility: visible; pointer-events: auto;
        transform: translateX(-50%) translateY(0);
      }
      @media (max-width: 749px) {
        sticky-add-to-cart .sticky-add-to-cart__bar { padding-bottom: max(8px, env(safe-area-inset-bottom)); }
        sticky-add-to-cart .sk-sticky-choice { min-height: 32px; padding: 5px 9px; }
        sticky-add-to-cart .sk-sticky-option select { font-size: 16px; min-height: 32px; }
      }
      @media (prefers-reduced-motion: reduce) {
        sticky-add-to-cart .sk-sticky-choice { transition: none; }
      }
    `;
    document.head.append(style);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
